<!-- Image/Video media editor (Phase 3 editor template). -->

<template>
  <div class="bui-entity-listing-page media-editor-redesign">
    <div class="media-editor-redesign__inner">
      <TopBar :isReadOnly="isReadOnly" />

      <section class="editor-grid">
        <!-- LEFT COLUMN -->
        <div class="editor-col">
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Media Details</h2>
            </template>

            <div class="field-content">
              <div class="d-flex field-row">
                <label for="me-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
                <TextField id="me-name" data-test-id="media-name" v-model="data.name" class="basis-1/2" width="100%" :disabled="isReadOnly" />
              </div>
              <div class="d-flex field-row field-row--top">
                <label class="basis-1/2 text-sm font-bold">Tags</label>
                <div class="basis-1/2">
                  <TagsField
                    :tags="data.tags"
                    width="100%"
                    :disabled="isReadOnly"
                    data-test-id="media-tags"
                    input-test-id="media-tags-input"
                  />
                </div>
              </div>
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Sharing</label>
                <SelectField
                  class="basis-1/2 me-select"
                  :options="sharingLevelOptions"
                  v-model="data.sharingLevel"
                  :disabled="isReadOnly"
                />
              </div>
              <div class="d-flex field-row field-row--top">
                <label class="basis-1/2 text-sm font-bold">Expires On</label>
                <div class="basis-1/2 me-expiration">
                  <CheckboxField
                    v-model="isExpirationTimeEnabled"
                    label="Enable Expiration Time"
                    data-test-id="media-enable-expiration"
                    v-if="!isReadOnly"
                  />
                  <DateField v-if="isExpirationTimeEnabled" v-model="data.expirationTime" width="100%" enableTime
                    :disabled="isReadOnly" />
                </div>
              </div>
              <div class="d-flex field-row field-row--top">
                <label class="basis-1/2 text-sm font-bold" data-testid="media-preview-label">Preview</label>
                <div class="basis-1/2">
                  <Dialog size="full" panelClass="bui-dialog-panel">
                    <template #expander="{openModal}">
                      <div @click="openModal" class="thumbnail-preview">
                        <ImageWithLoadingAndErrorIndicators
                          :src="data.previewUri"
                          imageClass="thumbnail-image"
                          :revalidateIfLessThanAge=60000
                          :dateLastModified="data.dateLastModified"
                          :refreshAfterIfRevalidate=30000
                        />
                      </div>
                    </template>
                    <template #dialog.title>Preview</template>
                    <template #dialog.content>
                      <MediaItemPreview :item="data" />
                    </template>
                  </Dialog>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="editor-col">
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Auditing</h2>
            </template>

            <div class="field-content">
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Approval</label>
                <div class="basis-1/2">
                  <SelectField
                    v-if="user.isAuditor() && !isReadOnly"
                    class="me-select"
                    :options="approvalStateOptions"
                    v-model="data.approvalState"
                    width="100%"
                  />
                  <template v-else>
                    {{ approvalStateLabel }}
                  </template>
                </div>
              </div>
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Last Audited By</label>
                <div class="basis-1/2"></div>
              </div>
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Last Audited On</label>
                <div class="basis-1/2"></div>
              </div>
            </div>
          </InfoCard>
        </div>
      </section>

      <!-- Sticky footer -->
      <footer class="editor-footer">
        <div class="editor-footer__inner">
          <ExitButton v-bind="{ hasUnsavedChanges: isReadOnly ? false : hasUnsavedChanges, unsetHasUnsavedChanges }"
            @exit="onExit" @saveAndExit="onSaveAndExit" />
          <SaveImageOrVideoButton v-if="!isReadOnly" ref="saveImageOrVideoButtonRef" />
        </div>
      </footer>
    </div>
  </div>
</template>



<script lang="ts" setup>
import TopBar from './TopBar/TopBar.vue';
import TextField from '@/components/form-fields/TextField.vue';
import useImageOrVideo from './composables/imageOrVideo';
import TagsField from '@/components/form-fields/TagsField/TagsField.vue';
import CheckboxField from '@/components/form-fields/CheckboxField.vue';
import DateField from '@/components/form-fields/DateField.vue';
import ImageWithLoadingAndErrorIndicators from '@/components/ImageWithLoadingAndErrorIndicators/ImageWithLoadingAndErrorIndicators.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import MediaItemPreview from '@/components/ListingPages/ItemsLister/MediaItemPreview/MediaItemPreview.vue';
import SelectField from '@/components/form-fields/SelectField/SelectField.vue';
import { approvalStateOptions } from '@/data/options/approvalStateOptions';
import SaveImageOrVideoButton from './SaveImageOrVideoButton/SaveImageOrVideoButton.vue';
import ExitButton from '@/components/Dialog/ButtonsWithDialog/ExitButton/ExitButton.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import SharingLevelsMapping from '@/data/mappings/SharingLevels.map'
import router from '@/router';
import { ref, computed } from 'vue';
import useUser from '@/composables/user';
const user = useUser();

defineProps<{
  isReadOnly: boolean
}>();

const { data, initializeHasUnsavedChangesWatcher, hasUnsavedChanges, unsetHasUnsavedChanges } = useImageOrVideo();
initializeHasUnsavedChangesWatcher();

const sharingLevelOptions = [
  // TODO: think about a cleaner way to write this.
  {
    label: SharingLevelsMapping[1],
    value: 1
  },
  {
    label: SharingLevelsMapping[2],
    value: 2
  },
];

const isExpirationTimeEnabled = computed({
  get() {
    return (data.expirationTime ? true : false);
  },
  set(newValue) {
    if(newValue) {
      const timeZoneOffsetMs = new Date().getTimezoneOffset() * 60000; // in milliseconds
      const currentTimeMs = new Date().getTime();
      const currentTimeWithTimeZoneOffset = currentTimeMs - timeZoneOffsetMs;
      data.expirationTime = new Date(currentTimeWithTimeZoneOffset).toISOString().slice(0,16);
    } else {
      data.expirationTime = null;
    }
    return newValue;
  }
});
const saveImageOrVideoButtonRef = ref();

const approvalStateLabel = computed(()=> {
  const matchedApprovalState = approvalStateOptions.find(option => option.value == data.approvalState) as (typeof approvalStateOptions)[number];
  return matchedApprovalState.label;
})

function onExit() {
  router.push({ path: '/media' })
}

async function onSaveAndExit() {
  await saveImageOrVideoButtonRef.value.saveSlide();
  router.push({ path: '/media' })
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.media-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.media-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* Legacy SelectField shell → tokens. */
.me-select {
  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }
}

.me-expiration {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  :deep(input[type='checkbox']) {
    accent-color: var(--bui-chk-checked-fill, #de3da9);
  }

  :deep(input[type='datetime-local']),
  :deep(input[type='date']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    width: 100%;
  }
}

.thumbnail-preview {
  cursor: pointer;
  max-width: 220px;

  :deep(.thumbnail-image) {
    border-radius: var(--bui-ter-radius, 8px);
    max-width: 100%;
  }
}

/* Tags field atoms → tokens. */
.field-content {
  :deep(.items-field),
  :deep(.tags-field input) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
  }
}
</style>
